import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { Reveal } from "../components/site/Reveal";

const CONTACT_IMAGE = "/images/hero.jpg";

const PROJECT_TYPES = [
  "Conseil et lecture du lieu",
  "Le Bâti",
  "Le Paysage",
  "L'Énergétique",
  "Projet global"
];

const HeroHeader = () => (
  <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
    <img
      src={CONTACT_IMAGE}
      alt="Prendre contact avec Rozier Concept"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
    <div className="absolute inset-0 flex items-end">
      <div className="container-rozier pb-16 md:pb-20 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors text-xs uppercase tracking-[0.28em] mb-6"
          data-testid="contact-back-home"
        >
          <ArrowLeft size={14} />
          Accueil
        </Link>
        <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight font-light">
          Parlons de votre <span className="italic gold-text-shimmer">projet</span>
        </h1>
        <p className="mt-6 font-serif italic text-white/85 text-2xl md:text-3xl max-w-3xl leading-snug">
          Une lecture attentive avant toute décision, pour un lieu qui vous
          ressemble et qui dure.
        </p>
      </div>
    </div>
  </section>
);

const Intro = () => (
  <section className="py-24 md:py-28 bg-white" data-testid="contact-intro">
    <div className="container-rozier grid grid-cols-12 gap-10">
      <Reveal className="col-span-12 md:col-span-5">
        <p className="overline mb-6">Premier échange</p>
        <h2 className="font-serif text-3xl md:text-5xl text-charcoal leading-[1.05] tracking-tight font-light">
          Décrivez-nous votre <span className="italic gold-text">lieu</span> et vos
          intentions.
        </h2>
      </Reveal>
      <Reveal className="col-span-12 md:col-span-6 md:col-start-7" delay={150}>
        <p className="text-graphite text-base md:text-lg leading-[1.9]">
          Que vous ayez un terrain en prévision d'achat, un bâti existant, une
          maison, un appartement, une entreprise, une exploitation ou un projet
          encore en réflexion : posons ensemble les bases d'un lieu harmonieux,
          performant et durable.
        </p>
        <p className="mt-6 text-graphite text-base md:text-lg leading-[1.9]">
          Nous prenons le temps d'écouter, d'analyser et de proposer une approche
          sur mesure, sans précipitation.
        </p>
      </Reveal>
    </div>
  </section>
);

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: PROJECT_TYPES[0],
    message: ""
  });
  const [status, setStatus] = useState("idle");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setStatus("sending");
    setTimeout(() => {
      setStatus("sent");
    }, 600);
  };

  if (status === "sent") {
    return (
      <div
        className="border border-stone p-10 md:p-14 bg-ivory"
        data-testid="contact-form-sent"
      >
        <p className="overline mb-4 text-gold">Message envoyé</p>
        <p className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
          Merci pour votre message.
        </p>
        <p className="mt-6 text-graphite text-base md:text-lg leading-relaxed max-w-xl">
          Nous reviendrons vers vous dans les meilleurs délais pour échanger sur
          votre projet et organiser un premier rendez-vous.
        </p>
        <button
          type="button"
          onClick={() => {
            setForm({
              name: "",
              email: "",
              phone: "",
              projectType: PROJECT_TYPES[0],
              message: ""
            });
            setStatus("idle");
          }}
          className="mt-10 inline-flex items-center gap-3 border border-charcoal text-charcoal px-8 py-4 uppercase tracking-[0.28em] text-xs hover:bg-charcoal hover:text-white transition-colors duration-300"
          data-testid="contact-form-new"
        >
          Nouveau message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="border border-stone p-8 md:p-12 bg-white"
      data-testid="contact-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="contact-name" className="overline block mb-3">
            Nom complet
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            value={form.name}
            onChange={onChange}
            className="w-full border-b border-stone bg-transparent py-3 text-charcoal placeholder-graphite/60 focus:outline-none focus:border-gold transition-colors"
            placeholder="Votre nom"
            data-testid="contact-input-name"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="overline block mb-3">
            Courriel
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={onChange}
            className="w-full border-b border-stone bg-transparent py-3 text-charcoal placeholder-graphite/60 focus:outline-none focus:border-gold transition-colors"
            placeholder="vous@exemple.fr"
            data-testid="contact-input-email"
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className="overline block mb-3">
            Téléphone
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={onChange}
            className="w-full border-b border-stone bg-transparent py-3 text-charcoal placeholder-graphite/60 focus:outline-none focus:border-gold transition-colors"
            placeholder="06 12 34 56 78"
            data-testid="contact-input-phone"
          />
        </div>
        <div>
          <label htmlFor="contact-project" className="overline block mb-3">
            Nature du projet
          </label>
          <select
            id="contact-project"
            name="projectType"
            value={form.projectType}
            onChange={onChange}
            className="w-full border-b border-stone bg-transparent py-3 text-charcoal focus:outline-none focus:border-gold transition-colors"
            data-testid="contact-input-project"
          >
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8">
        <label htmlFor="contact-message" className="overline block mb-3">
          Décrivez votre projet
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          value={form.message}
          onChange={onChange}
          className="w-full border border-stone bg-transparent p-4 text-charcoal placeholder-graphite/60 focus:outline-none focus:border-gold transition-colors resize-none"
          placeholder="Lieu, surface, contraintes, intentions, échéances."
          data-testid="contact-input-message"
        />
      </div>

      <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
        <p className="text-xs text-graphite max-w-md leading-relaxed">
          En envoyant ce message, vous acceptez d'être recontacté par Rozier
          Concept au sujet de votre demande.
        </p>
        <button
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center gap-3 bg-charcoal text-white px-10 py-4 uppercase tracking-[0.28em] text-xs hover:bg-gold transition-colors duration-300 disabled:opacity-60"
          data-testid="contact-form-submit"
        >
          {status === "sending" ? "Envoi en cours" : "Envoyer le message"}
          {status !== "sending" && <ArrowUpRight size={14} />}
        </button>
      </div>
    </form>
  );
};

const ContactBlock = () => (
  <section className="py-12 md:py-16 bg-white" data-testid="contact-form-section">
    <div className="container-rozier grid grid-cols-12 gap-10">
      <Reveal className="col-span-12 lg:col-span-7">
        <ContactForm />
      </Reveal>
      <Reveal className="col-span-12 lg:col-span-4 lg:col-start-9" delay={150}>
        <p className="overline mb-6">Nous joindre</p>
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-3 text-charcoal">
              <Mail size={16} className="text-gold" />
              <p className="overline">Courriel</p>
            </div>
            <a
              href="mailto:pv.rozier.01@gmail.com"
              className="mt-2 inline-block text-graphite text-sm hover:text-gold transition-colors"
              data-testid="contact-info-email"
            >
              pv.rozier.01@gmail.com
            </a>
          </div>
          <div>
            <div className="flex items-center gap-3 text-charcoal">
              <Phone size={16} className="text-gold" />
              <p className="overline">Téléphone</p>
            </div>
            <a
              href="tel:+33609136334"
              className="mt-2 inline-block text-graphite text-sm hover:text-gold transition-colors"
              data-testid="contact-info-phone"
            >
              06 09 13 63 34
            </a>
          </div>
          <div>
            <div className="flex items-center gap-3 text-charcoal">
              <MapPin size={16} className="text-gold" />
              <p className="overline">Adresse</p>
            </div>
            <p className="mt-2 text-graphite text-sm leading-relaxed" data-testid="contact-info-address">
              7 rue du docteur Hudellet
              <br />
              01000 Bourg-en-Bresse
            </p>
          </div>
        </div>

        <div className="mt-12 border-t border-stone pt-8">
          <p className="overline mb-3">Découvrir la méthode</p>
          <Link
            to="/la-methode-rozier"
            className="group inline-flex items-center gap-3 text-charcoal hover:text-gold transition-colors"
            data-testid="contact-link-methode"
          >
            <span className="font-serif italic text-lg">La Méthode Rozier</span>
            <ArrowUpRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const ClosingQuote = () => (
  <section className="py-24 md:py-32 bg-ivory" data-testid="contact-closing">
    <div className="container-rozier">
      <Reveal className="max-w-3xl mx-auto text-center">
        <p className="font-serif italic text-charcoal text-3xl md:text-5xl leading-[1.15] font-light">
          « Chez Rozier Concept, nous concevons des espaces en travaillant
          <span className="gold-text"> avec la nature</span>, et non contre elle. »
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <HeroHeader />
      <Intro />
      <ContactBlock />
      <ClosingQuote />
    </div>
  );
}
